/**
 * Yup schemas for the Atlas Console API surface (engine + agent + ARGUS).
 *
 * These mirror the response shapes from the per-deployment Atlas service.
 * The static TS types are inferred from the schemas so there's a single
 * source of truth — and the schemas are themselves suitable for auto-gen
 * tooling when we finalize the UI.
 *
 * Notes:
 *   • `unknown` and `Record<…>` shapes use `yup.mixed<…>()` — we preserve
 *     the static type for the UI without modeling free-form keys at runtime.
 *   • The agent step log is a 12-variant discriminated union (`type` field).
 *     Each variant gets its own schema; the union type is the intersection
 *     of InferType + the literal `type` tag (same pattern as
 *     `agent-conversation.schema.ts`).
 */
import * as yup from 'yup';

// ── Atlas engine ──

export const portfolioCompanySchema = yup.object({
  /** FA-side deal/company name (what Atlas extracted from source rows). */
  dealName: yup.string().required(),
  /** Fund scope — used as `fund` in compareSOI metricsPreview. */
  legalEntity: yup.string().required(),
  /** Cost of unrealized positions — the SOI-comparable cost. Field name
   *  matches the `column` table's fundData path (UI "Unrealized Cost").
   *  Backend renamed this from `costBasis` on 2026-06-04. */
  unrealizedCost: yup.number().required(),
  /** Cost recovered on exits — the SOI-comparable realized cost, and one of the
   *  compareSOI recon dimensions alongside unrealizedCost/fmv/shares/proceeds/capitalizedInterest. */
  realizedCost: yup.number().required(),
  /** Interest capitalized into cost — a compareSOI recon dimension and a
   *  COMPONENT of `unrealizedCost`, not a separate cost. Optional: older api
   *  builds omit it, and a required field would fail the whole parse. */
  capitalizedInterest: yup.number().optional(),
  fmv: yup.number().required(),
  /** Raw Σ MTM. Kept for completeness; not part of the compareSOI recon. */
  unrealized: yup.number().required(),
  /** Restructure-aware unrealized G/L: fmv − (amountInvested − realizedValue).
   *  Excluded from the compareSOI recon dimensions — `amountInvested` is a
   *  different cost basis than `unrealizedCost`, so this isn't recoverable
   *  from the primitives. */
  unrealizedGL: yup.number().required(),
  realized: yup.number().required(),
  realizedGain: yup.number().required(),
  proceeds: yup.number().required(),
  shares: yup.number().required(),
  /** Per-company invariant flags from the engine; rendered as warnings. */
  flags: yup.array().of(yup.string().required()).optional(),
  /** Count of GL atlas rows that contributed to this aggregation. */
  entries: yup.number().optional(),
});
export type PortfolioCompany = yup.InferType<typeof portfolioCompanySchema>;

const agentInstructionShape = yup.object({
  description: yup.string().required(),
  nextEndpoints: yup.array().of(yup.string().required()).required(),
});

/** Per-stage status produced by the propagation tracker.
 *   arrived    — rows for this run reached the stage.
 *   pending    — chain hasn't run / hasn't reached this stage yet.
 *   blocked    — stage ran, produced nothing, and will not fill without a
 *                human action (see `blockedBy`). Distinct from `pending`:
 *                nothing is in flight and waiting will not resolve it.
 *   superseded — the stage is now showing a later run's rows.
 *   n/a        — stage doesn't fire for this run (e.g. shadow/draft). */
const STAGE_STATUSES = ['arrived', 'pending', 'blocked', 'superseded', 'n/a'] as const;
export type StageStatus = (typeof STAGE_STATUSES)[number];

/** Fixed pipeline order — must match the API contract. */
const STAGE_NAMES = ['delivery', 'generalLedger', 'investran', 'metric'] as const;
export type StageName = (typeof STAGE_NAMES)[number];

export const PIPELINE_STAGE_ORDER: readonly StageName[] = STAGE_NAMES;

const RUN_STATUSES = ['running', 'completed', 'failed'] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

/** Template status as it appears on a transform run / recent-runs entry.
 *  Mirrors the top-level template `status` field but is duplicated here so
 *  the pipeline section of this file can be read without forward refs. */
const PIPELINE_TEMPLATE_STATUSES = ['draft', 'candidate', 'approved', 'rejected'] as const;

export const stageBySourceSchema = yup.object({
  sourceObject: yup.string().required(),
  rowCount: yup.number().required(),
});
export type StageBySource = yup.InferType<typeof stageBySourceSchema>;

/** Known blocker reason codes. The schema deliberately does NOT restrict to
 *  these (same forward-compat pattern as `agentEscalationSchema.category`) so a
 *  new backend reason can't fail validation on an older UI — unknown reasons
 *  render via a generic fallback. */
export const STAGE_BLOCKER_REASONS = ['company-mapping-approval'] as const;
export type StageBlockerReason = (typeof STAGE_BLOCKER_REASONS)[number];

/** Why a stage is empty and cannot progress on its own. `reason` is the stable
 *  handle the UI branches on; `message` is the operator-facing sentence. */
export const stageBlockerSchema = yup.object({
  reason: yup.string().required(),
  /** How many items need the human action. */
  count: yup.number().required(),
  message: yup.string().required(),
});
export type StageBlocker = yup.InferType<typeof stageBlockerSchema>;

export const stageViewSchema = yup.object({
  stage: yup.string().oneOf(STAGE_NAMES).required(),
  status: yup.string().oneOf(STAGE_STATUSES).required(),
  rowCount: yup.number().required(),
  bySource: yup.array().of(stageBySourceSchema.required()).optional(),
  /** Set when `status === 'blocked'` — names the action that unblocks it.
   *  `.default(undefined)` is load-bearing: without it yup fills the nested
   *  shape's default on validation and the required inner fields then fail
   *  for every stage that has no blocker. */
  blockedBy: stageBlockerSchema.optional().default(undefined),
});
export type StageView = yup.InferType<typeof stageViewSchema>;

export const recentRunSchema = yup.object({
  runId: yup.number().required(),
  runAt: yup.string().required(),
  templateStatus: yup.string().oneOf(PIPELINE_TEMPLATE_STATUSES).required(),
  status: yup.string().oneOf(RUN_STATUSES).required(),
  /** Config version the run used. Absent until the backend field ships. */
  templateVersion: yup.number().optional().nullable(),
});
export type RecentRun = yup.InferType<typeof recentRunSchema>;

export const pipelineSchema = yup.object({
  runId: yup.number().required(),
  batchId: yup.number().required(),
  templateStatus: yup.string().oneOf(PIPELINE_TEMPLATE_STATUSES).required(),
  runAt: yup.string().required(),
  runStatus: yup.string().oneOf(RUN_STATUSES).required(),
  stages: yup.array().of(stageViewSchema.required()).required(),
  recentRuns: yup.array().of(recentRunSchema.required()).optional(),
  /** Config version the run used. Absent until the backend field ships. */
  templateVersion: yup.number().optional().nullable(),
});
export type Pipeline = yup.InferType<typeof pipelineSchema>;

/** Shape of the `blockingRun` payload nested inside the API's 409 response.
 *  Lives at `response.data.message.blockingRun` — see runTransform() for the
 *  unwrap. */
export const blockingRunSchema = yup.object({
  runId: yup.number().required(),
  startedAt: yup.string().required(),
});
export type BlockingRun = yup.InferType<typeof blockingRunSchema>;

export const portfolioSummarySchema = yup.object({
  batchId: yup.number().required(),
  companies: yup.array().of(portfolioCompanySchema.required()).required(),
  totals: yup
    .object({
      costBasis: yup.number().required(),
      fmv: yup.number().required(),
      realized: yup.number().required(),
      proceeds: yup.number().required(),
    })
    .required(),
  flags: yup
    .object({
      negativeCostBasis: yup.number().required(),
      orphanedEntries: yup.number().required(),
      realizationWithoutCost: yup.number().required(),
    })
    .required(),
  agentInstruction: agentInstructionShape.optional(),
  /** Null when no approved-template run has ever existed, or when the server's
   *  pipeline read failed and degraded (see `degraded`). The API's degrade
   *  fallback is always null — a degraded pipeline is never a stale payload. */
  pipeline: pipelineSchema.nullable().defined(),
  /** Sections of the response serving a fallback instead of real data
   *  ("pipeline" | "portfolio" | "runStatus" today; open set for forward
   *  compat). Distinguishes a fallback `pipeline: null` (server-side read
   *  failed) from the genuine "no approved run ever". */
  degraded: yup.array().of(yup.string().required()).optional(),
  /** The tenant's latest run overall (any status), independent of the
   *  completed run `pipeline` is built from. Absent until the backend field
   *  ships — callers needing "a run to reconcile against" should fall back
   *  to `pipeline.recentRuns[0]` / `pipeline.runId`. */
  latestRun: recentRunSchema.optional().nullable(),
});
export type PortfolioSummary = yup.InferType<typeof portfolioSummarySchema>;

/** Response from `POST /atlas/transform` — initial snapshot at kick-off so
 *  the UI can seed the tracker without waiting for the next portfolio poll. */
export const transformRunResponseSchema = yup.object({
  pipeline: pipelineSchema.required(),
});
export type TransformRunResponse = yup.InferType<typeof transformRunResponseSchema>;

/** One condition in a rule's `match` array. The condition is: the named
 *  source column must take one of the listed values. Multiple conditions
 *  AND together to scope which source rows the rule applies to. */
export const ruleMatchConditionSchema = yup.object({
  field: yup.string().required(),
  values: yup.array().of(yup.mixed<string | number | boolean>().required()).required(),
});
export type RuleMatchCondition = yup.InferType<typeof ruleMatchConditionSchema>;

/** A per-rule override directive: what to set a field to, overriding the
 *  source-level column mapping. Open union — known shapes are rendered
 *  richly, anything else falls back to a readable JSON dump:
 *    • `{ literal: x }`  → set to a constant value
 *    • `number`          → numeric literal (e.g. 0)
 *    • `"negate"`        → sign flip (the v11 realized sign-flip)
 *    • `"-COLUMN"`       → negated source column
 *    • `"COLUMN"`        → mapped source column (may contain spaces/parens)
 *    • `{ subtract: [...] }` / other operator objects → expression
 *  Backend type: `Record<string, string | number | AccountingFormula | Expression>`. */
export type TemplateOverride = { literal: unknown } | string | number | Record<string, unknown>;

/** An engine `Expression` the agent may author where the field type nominally
 *  says `string`. Leaf forms reference a source column (`{field}`), a prior
 *  line (`{ref}`), or a constant (`{literal}`); operator forms (`{concat}`,
 *  `{subtract}`, …) combine operands. Mirrors the expression union in the api
 *  `expression-evaluator`; kept permissive (open operator key) so unknown
 *  operators still type-check. Any `string | Expression` field is therefore NOT
 *  a valid `ReactNode` — the renderer must route it through `templateValueText`,
 *  which is exactly how `tsc` surfaces every unguarded interpolation site. */
export type Expression =
  { field: string } | { ref: string } | { literal: unknown } | { [operator: string]: unknown };

/** One emitted line of a `multiLineTransaction` / `transactionBatch` module.
 *  `fieldMap`/`amount` directives reuse the override directive shapes; kept
 *  permissive so unknown line shapes still type-check and route to fallback.
 *  The string-typed fields are `string | Expression` because the engine can
 *  emit an expression object where a bare column name is expected. */
export interface ModuleConfigLine {
  id?: string;
  outputType?: string | Expression;
  suffix?: string | Expression;
  fieldMap?: Record<string, TemplateOverride>;
  amount?: { field?: string | Expression; aggregate?: string | Expression; [key: string]: unknown };
  transactionIdTemplate?: string | Expression;
  [key: string]: unknown;
}

/** Module-specific configuration. Open-keyed so any module's params survive to
 *  the renderer; known modules (`mtmDelta`, `multiLineTransaction`,
 *  `transactionBatch`) are narrowed for rich rendering. `positionKey` /
 *  `fmvComputation` are `string | Expression` (the engine's canonical stored
 *  positionKey is a `{concat:[…]}` expression). */
export interface ModuleConfig {
  /** mtmDelta */
  positionKey?: string | Expression;
  fmvComputation?: string | Expression;
  skipWhenDeltaZero?: boolean;
  /** multiLineTransaction / transactionBatch */
  lines?: ModuleConfigLine[];
  [key: string]: unknown;
}

/** Justification the agent attaches to a rule. Open-keyed; the renderer
 *  surfaces `reasoning`, `chainStep`, `transTypesCovered`. */
export interface RuleJustification {
  reasoning?: string;
  chainStep?: string | number;
  transTypesCovered?: unknown[];
  evidenceRowIds?: unknown[];
  [key: string]: unknown;
}

export const templateRuleSchema = yup.object({
  /** 1-based ordinal in the template's rules array. */
  number: yup.number().optional(),
  id: yup.string().nullable().optional(),
  /** Derived server-side: top-level ?? first moduleConfig line's outputType ??
   *  '(unknown)'. Tolerant of either casing of "unknown". */
  outputType: yup.string().required(),
  sourceObject: yup.string().nullable().optional(),
  module: yup.string().required(),
  match: yup.array().of(ruleMatchConditionSchema.required()).required(),
  restructureId: yup.string().nullable().optional(),
  positionKey: yup.string().nullable().optional(),
  /** Per-rule exceptions to the source-level column mapping (field → directive).
   *  Permissive at runtime so unknown directive shapes still type-check and
   *  render via the fallback. */
  overrides: yup.mixed<Record<string, TemplateOverride>>().optional(),
  /** Module-specific params (mtmDelta / multi-line / transactionBatch). */
  moduleConfig: yup.mixed<ModuleConfig>().optional(),
  groupBy: yup.array().of(yup.string().required()).optional(),
  bucket: yup.string().nullable().optional(),
  justification: yup.mixed<RuleJustification>().optional(),
  /** Catch-all for rule-level fields no one anticipated — always present
   *  (may be {}); rendered via the recursive fallback. */
  extras: yup.mixed<Record<string, unknown>>().optional(),
  purpose: yup.string().nullable().optional(),
  _provenance: yup
    .object({
      cycle: yup.number().optional(),
      attemptIndex: yup.number().optional(),
    })
    .optional(),
});
export type TemplateRule = yup.InferType<typeof templateRuleSchema>;

/** Default value pinned to a column when the source row doesn't supply one. */
export const sourceDefaultSchema = yup.object({
  name: yup.string().required(),
  value: yup.mixed<string | number | boolean>().nullable().defined(),
});
export type SourceDefault = yup.InferType<typeof sourceDefaultSchema>;

/** One mapping from a source-file column to a canonical Atlas field. */
export const columnMappingSchema = yup.object({
  atlasField: yup.string().required(),
  sourceColumn: yup.string().required(),
});
export type ColumnMapping = yup.InferType<typeof columnMappingSchema>;

/** Composite transaction-id derivation: concatenate `fields` with `separator`. */
export const transactionIdSchema = yup.object({
  fields: yup.array().of(yup.string().required()).required(),
  separator: yup.string().required(),
});
export type TransactionId = yup.InferType<typeof transactionIdSchema>;

/** One source-file classification: columns + defaults + transaction-id, plus
 *  a grab-bag of engine-internal fields we surface raw if at all. */
export const sourceClassificationSchema = yup.object({
  sourceObject: yup.string().required(),
  columnMappings: yup.array().of(columnMappingSchema.required()).required(),
  defaults: yup.array().of(sourceDefaultSchema.required()).optional(),
  transactionId: transactionIdSchema.nullable().optional(),
  typeField: yup.mixed().optional(),
  accountField: yup.mixed().optional(),
  accountCategory: yup.mixed().optional(),
  transactionSubstance: yup.mixed().optional(),
  computedAmounts: yup.mixed().optional(),
  extras: yup.mixed().optional(),
});
export type SourceClassification = yup.InferType<typeof sourceClassificationSchema>;

export const rowFilterSchema = yup.object({
  /** Optional in the engine — present on most filters but not required. */
  type: yup.string().optional(),
  field: yup.string().required(),
  value: yup.string().required(),
  operator: yup.string().required(),
});
export type RowFilter = yup.InferType<typeof rowFilterSchema>;

const TEMPLATE_STATUSES = ['draft', 'candidate', 'approved', 'rejected'] as const;
export type TemplateStatus = (typeof TEMPLATE_STATUSES)[number];

/** The header block on the `?format=structured` template response — carries
 *  the identity/lifecycle fields the UI uses for version pinning, approval
 *  gating, and the chip in the header. */
export const atlasTemplateHeaderSchema = yup.object({
  id: yup.number().required(),
  version: yup.number().required(),
  fundAccountantType: yup.string().required(),
  status: yup.string().oneOf(TEMPLATE_STATUSES).required(),
  name: yup.string().nullable().optional(),
  approvedAt: yup.string().nullable().optional(),
  createdAt: yup.string().optional(),
  updatedAt: yup.string().optional(),
  ruleCount: yup.number().optional(),
  context: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
});
export type AtlasTemplateHeader = yup.InferType<typeof atlasTemplateHeaderSchema>;

/** A field expression inside a companion entry's `fieldMap`: a literal number,
 *  a source column name, or a `-COLUMN` negation — same union as overrides. */
export type ReclassFieldExpression = TemplateOverride;

/** One `rocReclassification` branch. Two mutually-exclusive variants,
 *  discriminated on which key is present:
 *    • reclassify variant — has `reclassifyTo`
 *    • companion variant  — has `companionEntries` */
export interface RocReclassificationEntry {
  /** Agent-authored jsonb with no save-time type gate — the same class as
   *  positionKey. Typed `string | Expression` (not bare `string`) so the
   *  renderer can't interpolate it raw: `tsc` forces every render through
   *  `templateValueText`, which is what keeps `[object Object]` / React #31
   *  off the screen even if a future value arrives as an expression object. */
  sourceType?: string | Expression;
  scope?: { matchFields?: string[] };
  /** Sign condition gating the whole branch. */
  condition?: 'negative_amount' | 'positive_amount' | string;
  cooccursWith?: {
    matchMode?: string;
    /** Un-type-gated agent jsonb (see `sourceType`) — `string | Expression`. */
    accountName?: string | Expression;
    transType?: string | Expression;
    /** Sign requirement on the co-occurring row. */
    amountSign?: string | Expression;
    /** Polarity: when true, the rule fires only if `accountName` is ABSENT
     *  from the group; when false, only if PRESENT. */
    requireAbsence?: boolean;
    [key: string]: unknown;
  };
  /** reclassify variant — output type matched rows are remapped to. */
  reclassifyTo?: string;
  /** companion variant — additive companion rows emitted per matched row. */
  companionEntries?: Array<{
    /** Un-type-gated agent jsonb (see `sourceType`) — `string | Expression`. */
    suffix?: string | Expression;
    outputType?: string | Expression;
    fieldMap?: Record<string, ReclassFieldExpression>;
    skipIfZero?: boolean;
    [key: string]: unknown;
  }>;
  /** Guard against re-reclassifying a row a prior branch already touched. */
  skipIfAlreadyReclassified?: boolean;
  [key: string]: unknown;
}

/** Open-keyed reclassification config. Known keys (`rocReclassification`,
 *  `LEGAL_ENTITY`) render richly; other keys WILL appear and fall back to a
 *  readable dump so nothing is lost. */
export interface Reclassification {
  /** Object (back-compat) or ordered list of branches. */
  rocReclassification?: RocReclassificationEntry | RocReclassificationEntry[];
  /** Flat entity rename/consolidation map: old name → canonical name. */
  LEGAL_ENTITY?: Record<string, string>;
  [key: string]: unknown;
}

/** One excluded transaction type. Backend entity is jsonb (often null), passed
 *  through the serializer with no save-time type validation — the same
 *  un-type-gated agent-authored class as positionKey. The string-ish fields are
 *  typed `string | Expression` (not bare `string`) so `tsc` forces every render
 *  through `templateValueText` and a raw interpolation can never slip in. */
export interface ExcludedTransType {
  transType: string | Expression;
  sourceObject?: string | Expression;
  reason?: string | Expression;
  /** Pipeline step this exclusion belongs to (chain provenance). */
  chainStep?: string | number;
  [key: string]: unknown;
}

/** Top-level shape returned by `GET /atlas/template?format=structured`. */
export const atlasTemplateSchema = yup.object({
  header: atlasTemplateHeaderSchema.required(),
  sourceClassifications: yup.array().of(sourceClassificationSchema.required()).required(),
  rowFilter: yup.array().of(rowFilterSchema.required()).optional(),
  rules: yup.array().of(templateRuleSchema.required()).required(),
  /** Open-keyed reclassification config — rendered in the Reclassification
   *  section, with a fallback for unknown keys. Permissive at runtime. */
  reclassification: yup.mixed<Reclassification>().optional(),
  /** Trans types excluded during classification (jsonb, often null). */
  excludedTransTypes: yup.mixed<ExcludedTransType[]>().nullable().optional(),
  /** Engine-defined; opaque to the UI. */
  batchDetection: yup.mixed().optional(),
  /** Engine-defined; opaque to the UI. */
  advanced: yup.mixed().optional(),
  /** Catch-all for template-level fields no one anticipated (`discoveryNotes`,
   *  `faModel`, `shapes`, …). Always present (may be {}); rendered via the
   *  recursive fallback so nothing is ever dropped. */
  extras: yup.mixed<Record<string, unknown>>().optional(),
});
export type AtlasTemplate = yup.InferType<typeof atlasTemplateSchema>;

// ── Template versions (GET /atlas/template/versions) ──
//
// Lightweight per-version list used to gate the "Run transform" button on the
// newest approved config. `liveVersion` is that newest-approved version, or
// null when nothing is approved yet.
const TEMPLATE_VERSION_STATUSES = ['draft', 'candidate', 'approved', 'rejected'] as const;
export type TemplateVersionStatus = (typeof TEMPLATE_VERSION_STATUSES)[number];

export const templateVersionSummarySchema = yup.object({
  version: yup.number().required(),
  status: yup.string().oneOf(TEMPLATE_VERSION_STATUSES).required(),
  createdAt: yup.string().optional(),
  approvedAt: yup.string().nullable().optional(),
  ruleCount: yup.number().optional(),
  context: yup.string().nullable().optional(),
});
export type TemplateVersionSummary = yup.InferType<typeof templateVersionSummarySchema>;

export const templateVersionsResponseSchema = yup.object({
  versions: yup.array().of(templateVersionSummarySchema.required()).required(),
  // Newest approved version, or null when no config is approved yet.
  liveVersion: yup.number().nullable().defined(),
});
export type TemplateVersionsResponse = yup.InferType<typeof templateVersionsResponseSchema>;

// ── Atlas agent ──
//
// Shape mirrors `serializeSession()` in api/src/atlas/agent/atlas-agent.service.ts
// and the `AtlasStep` discriminated union in api/src/atlas/agent/types.ts.

const AGENT_RUN_STATUSES = ['idle', 'processing', 'awaiting_input', 'failed', 'archived'] as const;
export type AgentRunStatus = (typeof AGENT_RUN_STATUSES)[number];
export const agentRunStatusSchema = yup.string().oneOf(AGENT_RUN_STATUSES).required();

export const agentGuidanceSchema = yup.object({
  cycle: yup.number().required(),
  message: yup.string().required(),
});
export type AgentGuidance = yup.InferType<typeof agentGuidanceSchema>;

/** Engine-defined; opaque to the UI. Render as JSON if we need to. */
export type AgentLastDelta = unknown;

// Per-variant agent step schemas. Discriminator is the `type` field.

export const AGENT_STEP_TYPES = [
  'engagement_started',
  'cycle_started',
  'rationale',
  'tool_call',
  'tool_result',
  'cycle_ended',
  'plateau_detected',
  'paused_for_input',
  'cs_replied',
  'reverted',
  'scope_selected',
  'template_restored',
  'escalation',
  'engagement_ended',
  'error',
] as const;
export type AgentStepType = (typeof AGENT_STEP_TYPES)[number];

const agentStepBase = {
  timestamp: yup.string().required(),
};

export const engagementStartedStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['engagement_started'] as const)
    .required(),
  startCycle: yup.number().required(),
  triggeredBy: yup
    .string()
    .oneOf(['cs_run', 'recovery'] as const)
    .required(),
});

export const cycleStartedStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['cycle_started'] as const)
    .required(),
  cycle: yup.number().required(),
  mode: yup
    .string()
    .oneOf(['cold_start', 'refinement', 'plateau_prompt'] as const)
    .required(),
});

export const rationaleStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['rationale'] as const)
    .required(),
  cycle: yup.number().required(),
  text: yup.string().required(),
});

export const toolCallStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['tool_call'] as const)
    .required(),
  cycle: yup.number().required(),
  tool: yup.string().required(),
  input: yup.mixed<Record<string, unknown>>().required(),
  durationMs: yup.number().required(),
  /** Server-authored description of the call, rendered verbatim. Carries what
   *  `input` cannot — filters after the scope merge, row counts. Absent on
   *  steps logged before it shipped. */
  detail: yup.string().optional(),
});

export const toolResultStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['tool_result'] as const)
    .required(),
  cycle: yup.number().required(),
  tool: yup.string().required(),
  summary: yup.string().required(),
  error: yup.string().optional(),
});

export const cycleEndedStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['cycle_ended'] as const)
    .required(),
  cycle: yup.number().required(),
  finalMetrics: yup.mixed<Record<string, number>>().required(),
  /** Same per-dimension shape as `finalMetrics`, scored per SOI position
   *  instead of per dollar. Absent on steps written before it shipped. */
  perCompanyMetrics: yup.mixed<Record<string, number | null>>().optional(),
  /** Written together with `perCompanyMetrics`; the console never shows one
   *  without the other. */
  residual: yup
    .object({
      breaks: yup.number().required(),
      dollars: yup.number().required(),
      soiOnly: yup.number().required(),
    })
    .optional()
    .default(undefined),
  kept_changes: yup.array().of(yup.string().required()).required(),
});

export const plateauDetectedStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['plateau_detected'] as const)
    .required(),
  cycle: yup.number().required(),
  recentMetrics: yup.mixed<Record<string, number[]>>().required(),
  windowSize: yup.number().required(),
});

export const pausedForInputStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['paused_for_input'] as const)
    .required(),
  cycle: yup.number().required(),
  question: yup.string().required(),
});

export const csRepliedStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['cs_replied'] as const)
    .required(),
  cycle: yup.number().required(),
  message: yup.string().required(),
});

export const revertedStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['reverted'] as const)
    .required(),
  revertedStep: yup.object({ guidanceIndex: yup.number().required() }).required(),
  reason: yup.string().optional(),
});

export const engagementEndedStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['engagement_ended'] as const)
    .required(),
  endCycle: yup.number().required(),
  stopReason: yup.string().required(),
  summary: yup.string().required(),
});

export const errorStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['error'] as const)
    .required(),
  cycle: yup.number().required(),
  layer: yup
    .string()
    .oneOf(['bedrock', 'tool', 'state', 'compression'] as const)
    .required(),
  error: yup.string().required(),
});

export const scopeSelectedStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['scope_selected'] as const)
    .required(),
  cycle: yup.number().required(),
  reason: yup
    .string()
    .oneOf(['selected', 'advanced'] as const)
    .required(),
  dealName: yup.string().optional(),
  fundName: yup.string().optional(),
  fundId: yup.number().optional(),
  asOfDate: yup.string().optional(),
});

export const templateRestoredStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['template_restored'] as const)
    .required(),
  cycle: yup.number().required(),
  restoredToVersion: yup.number().required(),
  // regression_revert = orchestrator auto-reverted a regressing version;
  // undefined/agent_restore = the agent chose to restore.
  reason: yup
    .string()
    .oneOf(['agent_restore', 'regression_revert'] as const)
    .optional(),
});

export const escalationStepSchema = yup.object({
  ...agentStepBase,
  type: yup
    .string()
    .oneOf(['escalation'] as const)
    .required(),
  cycle: yup.number().required(),
  category: yup.string().required(),
  reason: yup.string().required(),
  evidence: yup.mixed<Record<string, unknown>>().required(),
});

/** The full discriminated union as a TS type. Use the per-variant schemas
 *  above for runtime validation; this type is what the UI narrows on. */
export type AgentStep =
  | (yup.InferType<typeof engagementStartedStepSchema> & { type: 'engagement_started' })
  | (yup.InferType<typeof cycleStartedStepSchema> & { type: 'cycle_started' })
  | (yup.InferType<typeof rationaleStepSchema> & { type: 'rationale' })
  | (yup.InferType<typeof toolCallStepSchema> & { type: 'tool_call' })
  | (yup.InferType<typeof toolResultStepSchema> & { type: 'tool_result' })
  | (yup.InferType<typeof cycleEndedStepSchema> & { type: 'cycle_ended' })
  | (yup.InferType<typeof plateauDetectedStepSchema> & { type: 'plateau_detected' })
  | (yup.InferType<typeof pausedForInputStepSchema> & { type: 'paused_for_input' })
  | (yup.InferType<typeof csRepliedStepSchema> & { type: 'cs_replied' })
  | (yup.InferType<typeof revertedStepSchema> & { type: 'reverted' })
  | (yup.InferType<typeof scopeSelectedStepSchema> & { type: 'scope_selected' })
  | (yup.InferType<typeof templateRestoredStepSchema> & { type: 'template_restored' })
  | (yup.InferType<typeof escalationStepSchema> & { type: 'escalation' })
  | (yup.InferType<typeof engagementEndedStepSchema> & { type: 'engagement_ended' })
  | (yup.InferType<typeof errorStepSchema> & { type: 'error' });

/** Terminal handoff payload — set on `AtlasSession.escalation` when the agent
 *  calls `escalate`, mirrored one-to-one onto the serialized session so the
 *  operator UI can render the reason/evidence without a second fetch. Cleared
 *  on `reset()` or when a fresh engagement opens, so it only ever reflects the
 *  most-recent engagement. `category` is the escalate union (kept as a plain
 *  string so a new backend category can't fail validation on an older UI). */
export const agentEscalationSchema = yup.object({
  category: yup.string().required(),
  reason: yup.string().required(),
  evidence: yup.mixed<Record<string, unknown>>().required(),
  cycle: yup.number().required(),
  at: yup.string().required(),
});
export type AgentEscalation = yup.InferType<typeof agentEscalationSchema>;

/** Inner `state` object on the agent response. */
export const agentInnerStateSchema = yup.object({
  template: yup.mixed<Record<string, unknown>>().nullable().defined(),
  metrics: yup.mixed<Record<string, number>>().required(),
  lastDelta: yup.mixed().optional(),
  lastFundName: yup.string().optional(),
  csGuidance: yup.array().of(agentGuidanceSchema.required()).required(),
  pendingQuestion: yup.string().optional(),
  lastError: yup.string().optional(),
  /** Engagement objects are opaque to the console UI; just typed loosely. */
  currentEngagement: yup.mixed().optional(),
  pastEngagements: yup.array().of(yup.mixed()).required(),
  /** Reset boundary: the backend floors the message stream at this seq and
   *  only ever raises it on `reset()`, so it doubles as a stable epoch id for
   *  the conversation cache — a change means "prior messages are gone". */
  streamFloorSeq: yup.number().optional(),
});
export type AgentInnerState = yup.InferType<typeof agentInnerStateSchema>;

/** What `GET /atlas/agent` returns (the `serializeSession` shape). */
export const agentResponseSchema = yup.object({
  status: agentRunStatusSchema,
  cycle: yup.number().required(),
  state: agentInnerStateSchema.required(),
  steps: yup.array().of(yup.mixed<AgentStep>().required()).required(),
  /** The most-recent engagement's escalation handoff, or null when the agent
   *  hasn't escalated. Nullable + optional so an older backend that doesn't
   *  serialize it (or a session that never escalated) still validates. */
  escalation: agentEscalationSchema.nullable().optional(),
});
export type AgentResponse = yup.InferType<typeof agentResponseSchema>;

// ── Atlas agent pins ──
//
// Durable operator/agent instructions the Atlas agent re-reads every cycle.
// `cs` = CS-pinned (absolute, operator law); `agent` = agent-recorded
// (binding-by-default but self-revisable by the agent).

const PIN_AUTHORS = ['cs', 'agent'] as const;
export type PinAuthor = (typeof PIN_AUTHORS)[number];

export const agentPinSchema = yup.object({
  text: yup.string().required(),
  author: yup.string().oneOf(PIN_AUTHORS).required(),
});
export type AgentPin = yup.InferType<typeof agentPinSchema>;

/** What `GET /atlas/agent/pins` returns. */
export const agentPinsResponseSchema = yup.object({
  pins: yup.array().of(agentPinSchema.required()).required(),
});
export type AgentPinsResponse = yup.InferType<typeof agentPinsResponseSchema>;

/** What `POST /atlas/agent/message` returns. There's no dedicated unpin
 *  endpoint — unpinning is a `unpin: <exact pin text>` message posted through
 *  this same channel, and `pins` here is the fresh post-unpin roster. */
export const agentMessageResponseSchema = yup.object({
  status: agentRunStatusSchema,
  seq: yup.number().required(),
  pins: yup.array().of(agentPinSchema.required()).required(),
});
export type AgentMessageResponse = yup.InferType<typeof agentMessageResponseSchema>;

// ── Staging readiness ──

export const stagingSourceObjectSchema = yup.object({
  sourceObject: yup.string().required(),
  rowCount: yup.number().required(),
  glDateRange: yup
    .object({
      min: yup.string().nullable().defined(),
      max: yup.string().nullable().defined(),
    })
    .nullable()
    .defined(),
});
export type StagingSourceObject = yup.InferType<typeof stagingSourceObjectSchema>;

export const stagingOverviewSchema = yup.object({
  fundAccountantType: yup.string().required(),
  hasInterpretation: yup.boolean().required(),
  sourceObjects: yup.array().of(stagingSourceObjectSchema.required()).required(),
});
export type StagingOverview = yup.InferType<typeof stagingOverviewSchema>;

export const soiUploadCountsSchema = yup.object({
  total: yup.number().required(),
  done: yup.number().required(),
  failed: yup.number().required(),
  pending: yup.number().required(),
  processing: yup.number().required(),
});
export type SoiUploadCounts = yup.InferType<typeof soiUploadCountsSchema>;

// ── Atlas status (GET /atlas/status) ──
//
// Agent-facing four-state snapshot; the console reads only `state` and
// `lastPromotionRun` (the outcome of an async HTTP re-promote, distinct from
// the agent's in-process promotes, which write no run row). Mirrors
// `StatusV2Response` in the api's `read-surface.dto.ts`.

const ATLAS_STATES = ['noTemplate', 'draftIterating', 'awaitingApproval', 'approved'] as const;
export type AtlasState = (typeof ATLAS_STATES)[number];

const PROMOTION_RUN_STATUSES = ['running', 'completed', 'failed'] as const;
export type PromotionRunStatus = (typeof PROMOTION_RUN_STATUSES)[number];

export const lastPromotionRunSchema = yup.object({
  promotionRunId: yup.number().required(),
  templateId: yup.number().required(),
  templateVersion: yup.number().nullable().defined(),
  status: yup.string().oneOf(PROMOTION_RUN_STATUSES).required(),
  runAt: yup.string().required(),
  /** The gate's `Cannot promote: …` line, truncated at 400 chars with a
   *  suffix pointing at the full verdict endpoint. Render as given — the UI
   *  must not re-truncate it. */
  failureReason: yup.string().nullable().defined(),
  pollEndpoint: yup.string().required(),
});
export type LastPromotionRun = yup.InferType<typeof lastPromotionRunSchema>;

export const atlasStatusSchema = yup.object({
  state: yup.string().oneOf(ATLAS_STATES).required(),
  /** Null when no HTTP-path promote has ever run for this FA. Stale is
   *  possible and expected — see the gating note on `state` above. */
  lastPromotionRun: lastPromotionRunSchema.nullable().defined(),
});
export type AtlasStatus = yup.InferType<typeof atlasStatusSchema>;

// ── ARGUS / SOI compare ──

// 'Grouping Difference': the BE-emitted status for a per-security
// difference that is only a re-slice of a company total that itself reconciles.
// Rendered NEUTRAL (not a break/variance) — see cellStatusCss in OutcomePanel.
const COMPARE_SOI_STATUSES = [
  'Match',
  'Close Match',
  'Variance',
  'No SOI Data',
  'Grouping Difference',
] as const;
export type CompareSoiStatus = (typeof COMPARE_SOI_STATUSES)[number];

export const compareSoiComparisonSchema = yup.object({
  /** The tenant's configured canonical field name (`compareSoiFieldConfig`),
   *  not the caller-supplied key — e.g. 'unrealizedCost', not 'totalCost'.
   *  e.g. 'unrealizedCost' | 'fmv' | 'noOfShares' | 'realizedCost' | 'proceeds' |
   *  'capitalizedInterest'. Unrestricted string since the set is tenant-configurable. */
  field: yup.string().required(),
  callerValue: yup.number().required(),
  soiValue: yup.number().nullable().defined(),
  status: yup.string().oneOf(COMPARE_SOI_STATUSES).required(),
  delta: yup.number().nullable().defined(),
});
export type CompareSoiComparison = yup.InferType<typeof compareSoiComparisonSchema>;

/** How a caller row linked to its SOI counterpart (backend `MatchedVia`):
 *   exact      — name matched the SOI name; the comparison is reliable.
 *   alias      — matched via a persisted alias / heuristic; reliable.
 *   llm-bridge — force-matched by the LLM; the comparison itself is UNCERTAIN.
 *   unmatched  — no SOI counterpart; not a comparison at all.
 *  Kept as a plain string oneOf so a future backend value won't fail validation. */
const COMPARE_SOI_MATCHED_VIA = ['exact', 'alias', 'llm-bridge', 'unmatched'] as const;
export type CompareSoiMatchedVia = (typeof COMPARE_SOI_MATCHED_VIA)[number];

export const compareSoiRowSchema = yup.object({
  companyName: yup.string().required(),
  fund: yup.string().required(),
  securityName: yup.string().optional(),
  matched: yup
    .object({
      companyId: yup.number().required(),
      matchedName: yup.string().required(),
    })
    .nullable()
    .defined(),
  comparisons: yup.array().of(compareSoiComparisonSchema.required()).required(),
  /** Match provenance. Optional so an older backend that doesn't send it still
   *  validates (rows are then treated as unmarked, i.e. no provenance chip). */
  matchedVia: yup.string().oneOf(COMPARE_SOI_MATCHED_VIA).optional(),
  /** SOI-side spellings that mapped to this row (>1 = fragmentation signal). */
  candidateSpellings: yup.array().of(yup.string().required()).optional(),
  /** Present only on N-to-1 alias mappings: every contributing caller name,
   *  highest-FMV first. `companyName` is the primary. */
  aggregatedCallerNames: yup.array().of(yup.string().required()).optional(),
});
export type CompareSoiRow = yup.InferType<typeof compareSoiRowSchema>;

const COMPARE_SOI_FUND_STATUSES = ['ok', 'noSoiUpload', 'fundNotFound', 'incompatibleGranularity'] as const;
export type CompareSoiFundStatus = (typeof COMPARE_SOI_FUND_STATUSES)[number];

export const compareSoiFundResultSchema = yup.object({
  fund: yup.string().required(),
  status: yup.string().oneOf(COMPARE_SOI_FUND_STATUSES).required(),
  message: yup.string().optional(),
});
export type CompareSoiFundResult = yup.InferType<typeof compareSoiFundResultSchema>;

const compareSoiUnmatchedRowSchema = yup.object({
  companyName: yup.string().required(),
  fund: yup.string().required(),
  securityName: yup.string().optional(),
});

export const compareSoiResponseSchema = yup.object({
  /** Pre-baked one-line outcome summary — quote verbatim in the UI. */
  summary: yup.string().required(),
  funds: yup.array().of(compareSoiFundResultSchema.required()).required(),
  results: yup.array().of(compareSoiRowSchema.required()).required(),
  unmatched: yup
    .object({
      callerRowsNotFound: yup.array().of(compareSoiUnmatchedRowSchema.required()).required(),
      soiRowsNotProvided: yup.array().of(compareSoiUnmatchedRowSchema.required()).required(),
    })
    .required(),
  /** The tenant's configured metric fields for this request — lets the UI
   *  tell "never configured for this tenant" apart from "SOI didn't have it".
   *  Optional so an older response (deployment skew) doesn't trip that check. */
  comparedFields: yup.array().of(yup.string().required()).optional(),
});
export type CompareSoiResponse = yup.InferType<typeof compareSoiResponseSchema>;

export const compareSoiRequestRowSchema = yup.object({
  companyName: yup.string().required(),
  fund: yup.string().required(),
  securityName: yup.string().optional(),
  metrics: yup
    .object({
      fmv: yup.number().optional(),
      unrealizedCost: yup.number().optional(),
      noOfShares: yup.number().optional(),
      realizedCost: yup.number().optional(),
      proceeds: yup.number().optional(),
      capitalizedInterest: yup.number().optional(),
      unrealizedValueCostBasis: yup.number().optional(),
    })
    .required(),
});
export type CompareSoiRequestRow = yup.InferType<typeof compareSoiRequestRowSchema>;

// ── Fund-level target metrics ──
//
// CS-curated fund totals (from a trusted source, e.g. the fund-admin statement)
// for a (fundId, asOfDate), diffed against the Atlas-computed fund-summary.
// Fund-level only and independent of the per-company ARGUS comparison.

/** The fund-tier metrics we surface — the ground-truth totals CS curates from
 *  the fund-admin statement (LP/GP contributions & distributions, management
 *  fee, LP NAV). Keys are `IFundMetrics` fields (the same vocabulary the
 *  fund-summary endpoint returns and the target jsonb stores), so target vs
 *  computed is an apples-to-apples diff. Distinct from the gross/SOI
 *  reconciliation dimensions (`fmv`/`unrealizedGL`/etc.) that ARGUS/compare_soi
 *  already covers. `lpNav` is a target dimension like the rest, but its Atlas
 *  (computed) side is provenance-gated in the UI — only draft-GL-derived lpNav
 *  (`lpNavSource === 'gl_transformation'`) is reconcilable against the draft
 *  run; waterfall/manual lpNav is suppressed. */
export const FUND_TARGET_METRIC_KEYS = [
  'lpContributions',
  'gpContributions',
  'lpDistributions',
  'gpDistributions',
  'managementFee',
  'lpNav',
] as const;
export type FundTargetMetricKey = (typeof FUND_TARGET_METRIC_KEYS)[number];

/** A subset of `IFundMetrics` — the dimensions this UI reads. The fund-summary
 *  response is a superset; yup strips the rest. Every value is optional/nullable
 *  because a curated target may carry only the dimensions CS actually has. */
export const fundMetricsSubsetSchema = yup.object({
  lpContributions: yup.number().nullable().optional(),
  gpContributions: yup.number().nullable().optional(),
  lpDistributions: yup.number().nullable().optional(),
  gpDistributions: yup.number().nullable().optional(),
  managementFee: yup.number().nullable().optional(),
  lpNav: yup.number().nullable().optional(),
});
export type FundMetricsSubset = yup.InferType<typeof fundMetricsSubsetSchema>;

/** `GET /atlas/batch/:transformRunId/fund-summary` response — the Atlas-computed
 *  side of the diff. A superset of the target metrics: it also carries
 *  `lpNavDraftScoped` and `lpNavSource`, the provenance of `lpNav`.
 *  `lpNavDraftScoped` is the honest gate — TRUE only when `lpNav` was genuinely
 *  derived from THIS draft run (ledger-ingestion fund, draft tiers actually
 *  used, no manual override); absent/false otherwise. `lpNavSource` is NOT a
 *  reliable draft signal (it reads `gl_transformation` for stale prod ledgers
 *  and v2-waterfall fallbacks too) so it's kept only for the tooltip text. When
 *  `lpNavDraftScoped` isn't true the UI shows "—" for lpNav. */
export const fundSummaryResponseSchema = fundMetricsSubsetSchema.shape({
  lpNavDraftScoped: yup.boolean().nullable().optional(),
  lpNavSource: yup.string().nullable().optional(),
});
export type FundSummaryResponse = yup.InferType<typeof fundSummaryResponseSchema>;

/** `GET /atlas/fund-target` response (and the shape `PUT` returns). */
export const fundReconciliationTargetSchema = yup.object({
  fundId: yup.number().required(),
  asOfDate: yup.string().required(),
  metrics: fundMetricsSubsetSchema.required(),
  notes: yup.string().nullable().defined(),
  capturedAt: yup.string().optional(),
});
export type FundReconciliationTarget = yup.InferType<typeof fundReconciliationTargetSchema>;

/** `PUT /atlas/fund-target` request body. */
export interface UpsertFundTargetInput {
  fundId: number;
  asOfDate: string;
  metrics: FundMetricsSubset;
  notes?: string;
}

// ── Legacy parity (GET /atlas/legacy-parity) ──
//
// Full-grain comparison of the live legacy `generalLedger` against the Atlas
// owner run's `generalLedgerAtlas` output — what CS reads to decide whether a
// client is safe to flip onto Atlas (which retires the legacy ledger). Mirrors
// `LegacyParityResponse` in api's `legacy-parity.service.ts`. Server-side
// `runAt`/`lastHeartbeatAt` are `Date` — typed as strings here (ISO on the wire).

export const legacyParityOwnerRunSchema = yup.object({
  id: yup.number().required(),
  // Same run-status enum as the pipeline's — `runFreshness` branches on
  // exactly these three.
  status: yup.string().oneOf(RUN_STATUSES).required(),
  templateVersion: yup.number().required(),
  templateStatus: yup.string().required(),
  runAt: yup.string().required(),
  lastHeartbeatAt: yup.string().nullable().defined(),
  ageMs: yup.number().required(),
  productionWriteHappened: yup.boolean().required(),
});
export type LegacyParityOwnerRun = yup.InferType<typeof legacyParityOwnerRunSchema>;

export const legacyParityLatestRunSchema = yup.object({
  id: yup.number().required(),
  status: yup.string().oneOf(RUN_STATUSES).required(),
  runAt: yup.string().required(),
});
export type LegacyParityLatestRun = yup.InferType<typeof legacyParityLatestRunSchema>;

export const legacyParityTotalsSchema = yup.object({
  buckets: yup.number().required(),
  matching: yup.number().required(),
  nonMatching: yup.number().required(),
  legacyOnly: yup.number().required(),
  atlasOnly: yup.number().required(),
  legacyRows: yup.number().required(),
  atlasRows: yup.number().required(),
});
export type LegacyParityTotals = yup.InferType<typeof legacyParityTotalsSchema>;

export const legacyParitySecondLoaderClassSchema = yup.object({
  label: yup.string().required(),
  rowCount: yup.number().required(),
  lastWriteAt: yup.string().nullable().defined(),
  sampleTransactionIds: yup.array().of(yup.string().required()).required(),
});
export type LegacyParitySecondLoaderClass = yup.InferType<typeof legacyParitySecondLoaderClassSchema>;

export const legacyParitySecondLoaderBlockerSchema = yup.object({
  present: yup.boolean().required(),
  rowCount: yup.number().required(),
  amountSum: yup.number().required(),
  lastWriteAt: yup.string().nullable().defined(),
  classes: yup.array().of(legacyParitySecondLoaderClassSchema.required()).required(),
});
export type LegacyParitySecondLoaderBlocker = yup.InferType<typeof legacyParitySecondLoaderBlockerSchema>;

export const legacyParityUnreachableHistoryBlockerSchema = yup.object({
  present: yup.boolean().required(),
  rowCount: yup.number().required(),
  amountSum: yup.number().required(),
  minLegacyQuarterEnd: yup.string().nullable().defined(),
  minAtlasSourceDate: yup.string().nullable().defined(),
});
export type LegacyParityUnreachableHistoryBlocker = yup.InferType<
  typeof legacyParityUnreachableHistoryBlockerSchema
>;

/** One side (legacy or Atlas) of a fund-quarter headline row. Cumulative
 *  through the quarter, mirroring the engine's own NAV rollup semantics. */
export const legacyParityHeadlineSideSchema = yup.object({
  lpNav: yup.number().required(),
  gpNav: yup.number().required(),
  slpNav: yup.number().required(),
  contributions: yup.number().required(),
  distributions: yup.number().required(),
  cost: yup.number().required(),
});
export type LegacyParityHeadlineSide = yup.InferType<typeof legacyParityHeadlineSideSchema>;

export const legacyParityHeadlineQuarterSchema = yup.object({
  quarterEnd: yup.string().required(),
  legacy: legacyParityHeadlineSideSchema.required(),
  atlas: legacyParityHeadlineSideSchema.required(),
});
export type LegacyParityHeadlineQuarter = yup.InferType<typeof legacyParityHeadlineQuarterSchema>;

export const legacyParityHeadlineFundSchema = yup.object({
  fundId: yup.number().required(),
  fundName: yup.string().required(),
  quarters: yup.array().of(legacyParityHeadlineQuarterSchema.required()).required(),
});
export type LegacyParityHeadlineFund = yup.InferType<typeof legacyParityHeadlineFundSchema>;

// Exposed as a field map so the parity grid can re-shape the same fields with
// its labels and formatting; `gridMeta` is a yup extension registered at
// bootstrap, so a grid schema has to be built lazily rather than at module load.
export function legacyParityBucketFields() {
  return {
    legalEntity: yup.string().nullable().defined(),
    dealId: yup.number().nullable().defined(),
    transType: yup.string().nullable().defined(),
    quarterEnd: yup.string().nullable().defined(),
    legacyDealName: yup.string().nullable().defined(),
    atlasDealName: yup.string().nullable().defined(),
    legacyAmount: yup.number().required(),
    legacyQuantity: yup.number().required(),
    legacyRows: yup.number().required(),
    legacyTransformRows: yup.number().required(),
    atlasAmount: yup.number().required(),
    atlasQuantity: yup.number().required(),
    atlasRows: yup.number().required(),
    match: yup.boolean().required(),
  };
}

export const legacyParityBucketSchema = yup.object(legacyParityBucketFields());
export type LegacyParityBucket = yup.InferType<typeof legacyParityBucketSchema>;

export const legacyParityResponseSchema = yup.object({
  quarterBasis: yup
    .object({
      type: yup
        .string()
        .oneOf(['calendar'] as const)
        .required(),
      fiscalYearEndMonth: yup
        .number()
        .oneOf([12] as const)
        .required(),
    })
    .required(),
  ownerRun: legacyParityOwnerRunSchema.required(),
  // Null when the tenant's latest run IS the owner run (the common case) —
  // present only when a newer run exists that failed to take over staging.
  latestRun: legacyParityLatestRunSchema.nullable().defined(),
  totals: legacyParityTotalsSchema.required(),
  blockers: yup
    .object({
      secondLoader: legacyParitySecondLoaderBlockerSchema.required(),
      unreachableHistory: legacyParityUnreachableHistoryBlockerSchema.required(),
    })
    .required(),
  headline: yup.array().of(legacyParityHeadlineFundSchema.required()).required(),
  unattributedFundBuckets: yup.number().required(),
  // Capped at 50k; `bucketsTruncated` says whether the cap bit. The CSV export
  // (format=csv) always streams the full, untruncated grain.
  buckets: yup.array().of(legacyParityBucketSchema.required()).required(),
  bucketsTruncated: yup.boolean().required(),
});
export type LegacyParityResponse = yup.InferType<typeof legacyParityResponseSchema>;
